<script lang="ts" setup>
import { computed, nextTick, reactive, ref, shallowRef, useTemplateRef, watch } from 'vue';
import { $fetch } from '@/home/plugins/fetch';
import { getInstance } from '@/home/plugins/i18n/index';
import AssistantComposer from './AssistantComposer.vue';
import AssistantMessageList from './AssistantMessageList.vue';
import AssistantQuickQuestions from './AssistantQuickQuestions.vue';
import { normalizeSuggestions } from './sse';
import type {
  AssistantCitation,
  AssistantFallbackPayload,
  AssistantHistoryMessage,
  AssistantMessage,
  AssistantPendingQuestion,
  AssistantQuestion,
  AssistantSessionUpdate,
  AssistantSuggestionItem,
} from './types';

const props = defineProps<{
  activeConversationId: string;
  greeting: string;
  pendingQuestion: AssistantPendingQuestion | null;
  questions: AssistantQuestion[];
}>();

const emit = defineEmits<{
  error: [message: string];
  pendingQuestionHandled: [questionId: number];
  sessionUpdated: [session: AssistantSessionUpdate];
}>();

interface SseEvent {
  event: string;
  data: unknown;
}

const CHANNEL_KEY = import.meta.env.VITE_ASSISTANT_CHANNEL_KEY || '';
const MESSAGE_CANCELED_CONTENT = '已取消生成。';
const MESSAGE_ERROR_CONTENT = '暂时无法回复，请稍后再试。';
const TRACK_EVENT_FALLBACK = 'chat_knowledge_fallback';
const TRACK_EVENT_SUGGESTIONS_SHOW = 'chat_suggestions_show';
const TRACK_EVENT_SUGGESTION_CLICK = 'chat_suggestion_click';

let localMessageId = 0;
let historyRequestId = 0;

interface ActiveStreamState {
  abortController: AbortController;
  assistantMessage: AssistantMessage;
  conversationId: string;
}

const messages = ref<AssistantMessage[]>([]);
const activeStream = shallowRef<ActiveStreamState | null>(null);
const cancelingResponse = ref(false);
const loadingHistory = ref(false);
const processingPendingQuestionId = ref<number | null>(null);
const sessionRecommendedQuestions = ref<string[]>([]);
const recommendedQuestions = ref<string[]>([]);
const showSuggestions = ref(false);
const currentFallback = ref<AssistantFallbackPayload | null>(null);
const messageListRef = useTemplateRef('messageList');
const composerRef = useTemplateRef('composer');

const visibleQuestions = computed<AssistantQuestion[]>(() => showSuggestions.value
  ? recommendedQuestions.value.map((prompt, index) => ({
    id: `assistant-suggestion-${index}-${prompt}`,
    prompt,
  }))
  : []);

const isResponding = computed(() => messages.value.some(message => message.status === 'streaming'));
const composerInputDisabled = computed(() => loadingHistory.value || !props.activeConversationId);
const composerSendDisabled = computed(() => composerInputDisabled.value || isResponding.value);
const locale = getInstance();

const createLocalMessageId = () => {
  localMessageId += 1;

  return `assistant-message-${Date.now()}-${localMessageId}`;
};

const trackAction = (eventName: string, params: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && window._oafs) {
    window._oafs.action(eventName, params);
  }
};

const trackKnowledgeFallback = (assistantMessage: AssistantMessage, fallback: AssistantFallbackPayload) => {
  trackAction(TRACK_EVENT_FALLBACK, {
    conversationId: props.activeConversationId,
    messageId: assistantMessage.messageId ?? '',
    channelKey: CHANNEL_KEY,
    reason: fallback.reason ?? '',
    retryAttempted: Boolean(fallback.retryAttempted),
  });
};

const trackSuggestionsShown = (count: number) => {
  trackAction(TRACK_EVENT_SUGGESTIONS_SHOW, {
    conversationId: props.activeConversationId,
    count,
  });
};

const truncateText = (text: string, maxLength = 18) => {
  const normalizedText = text.replace(/\s+/g, ' ').trim();

  if (normalizedText.length <= maxLength) {
    return normalizedText;
  }

  return `${normalizedText.slice(0, maxLength)}...`;
};

const createGreetingMessages = (): AssistantMessage[] => props.greeting
  ? [
    {
      id: createLocalMessageId(),
      role: 'assistant',
      content: props.greeting,
      status: 'done',
    },
  ]
  : [];

const scrollMessagesToBottom = () => {
  void messageListRef.value?.scrollToBottom();
};

const focusInput = async () => {
  await nextTick();
  await composerRef.value?.focusInput();
};

const parseCitations = (citations?: string): AssistantCitation[] | undefined => {
  if (!citations) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(citations);

    return Array.isArray(parsed) ? parsed : undefined;
  }
  catch {
    return undefined;
  }
};

const mapHistoryMessage = (message: AssistantHistoryMessage): AssistantMessage => ({
  id: message.messageId || createLocalMessageId(),
  messageId: message.messageId,
  role: message.role,
  content: message.content,
  citations: parseCitations(message.citations),
  createdAt: message.createdAt,
  status: 'done',
});

const updateSessionSummary = (conversationId: string, updatedAt = Date.now()) => {
  const userMessages = messages.value.filter(message => message.role === 'user');
  const firstUserMessage = userMessages[0];
  const lastUserMessage = userMessages[userMessages.length - 1];

  emit('sessionUpdated', {
    conversationId,
    title: firstUserMessage ? truncateText(firstUserMessage.content) : undefined,
    preview: lastUserMessage ? truncateText(lastUserMessage.content, 28) : undefined,
    updatedAt,
  });
};

const loadHistory = async (conversationId: string) => {
  const currentRequestId = historyRequestId += 1;

  if (!conversationId) {
    messages.value = [];

    return;
  }

  currentFallback.value = null;
  recommendedQuestions.value = [...sessionRecommendedQuestions.value];
  showSuggestions.value = recommendedQuestions.value.length > 0;

  // 切换会话时取消当前流，避免旧会话的 fallback/suggestions 事件污染新会话状态。
  const streamState = activeStream.value;

  if (streamState && streamState.assistantMessage.status === 'streaming') {
    // 先标记取消，让已缓冲的帧被 handleSseEvent 的 canceled 守卫拦截。
    markMessageCanceled(streamState.assistantMessage);

    streamState.abortController.abort();

    if (streamState.assistantMessage.messageId) {
      void requestCancelMessage(streamState.assistantMessage.messageId);
    }
  }

  loadingHistory.value = true;
  messages.value = [];

  const { isSuccess, data, msg } = await $fetch<{ messages?: AssistantHistoryMessage[] }>(`/chat/v1/sessions/${conversationId}/messages`, {
    method: 'get',
    data: { limit: 50 },
    headers: {
      Authorization: `Bearer ${CHANNEL_KEY}`,
    },
  });

  if (currentRequestId !== historyRequestId) {
    return;
  }

  if (!isSuccess) {
    messages.value = [
      {
        id: createLocalMessageId(),
        role: 'assistant',
        content: '历史消息加载失败，你可以继续发起新的咨询。',
        status: 'failed',
      },
    ];

    emit('error', msg || '历史消息加载失败。');
  }
  else if (data?.messages?.length) {
    messages.value = data.messages.map(mapHistoryMessage);
    const lastMessage = data.messages[data.messages.length - 1];
    const updatedAt = lastMessage.createdAt ? Date.parse(lastMessage.createdAt) : Date.now();

    updateSessionSummary(conversationId, Number.isNaN(updatedAt) ? Date.now() : updatedAt);
  }
  else {
    messages.value = createGreetingMessages();
  }

  loadingHistory.value = false;

  scrollMessagesToBottom();
};

const parseSseFrame = (frame: string): SseEvent | null => {
  let event = 'message';
  const dataLines: string[] = [];

  for (const line of frame.split(/\r?\n/)) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
    }
    else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim());
    }
  }

  if (dataLines.length === 0) {
    return null;
  }

  const rawData = dataLines.join('\n');
  let data: unknown;

  try {
    data = JSON.parse(rawData);
  }
  catch {
    data = rawData;
  }

  return { event, data };
};

const readSseStream = async (
  body: ReadableStream<Uint8Array>,
  onEvent: (event: SseEvent) => void | Promise<void>,
) => {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const findFrameSeparator = () => {
    const unixIndex = buffer.indexOf('\n\n');
    const windowsIndex = buffer.indexOf('\r\n\r\n');

    if (unixIndex < 0 && windowsIndex < 0) {
      return null;
    }

    if (windowsIndex >= 0 && (unixIndex < 0 || windowsIndex < unixIndex)) {
      return { index: windowsIndex, length: 4 };
    }

    return { index: unixIndex, length: 2 };
  };

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      let frameSeparator = findFrameSeparator();

      while (frameSeparator) {
        const frame = buffer.slice(0, frameSeparator.index);
        buffer = buffer.slice(frameSeparator.index + frameSeparator.length);
        const event = parseSseFrame(frame);

        if (event) {
          await onEvent(event);
        }

        frameSeparator = findFrameSeparator();
      }
    }

    buffer += decoder.decode();

    if (buffer.trim()) {
      const event = parseSseFrame(buffer);

      if (event) {
        await onEvent(event);
      }
    }
  }
  finally {
    reader.releaseLock();
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(
  value && typeof value === 'object',
);

const isAbortError = (error: unknown) => isRecord(error) && error.name === 'AbortError';

const getCurrentLanguage = () => locale.global.locale.value.split('-')[0];

const getErrorMessageFromPayload = (payload: unknown, fallback: string) => {
  if (typeof payload === 'string') {
    return payload.trim() || fallback;
  }

  if (!isRecord(payload)) {
    return fallback;
  }

  if (typeof payload.message === 'string' && payload.message.trim()) {
    return payload.message;
  }

  const error = payload.error;

  if (isRecord(error) && typeof error.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const parseErrorResponse = async (response: Response) => {
  const fallbackMessage = response.statusText || MESSAGE_ERROR_CONTENT;
  const contentType = response.headers.get('Content-Type') || '';

  if (contentType.includes('application/json')) {
    try {
      return getErrorMessageFromPayload(await response.json(), fallbackMessage);
    }
    catch {
      return fallbackMessage;
    }
  }

  try {
    return getErrorMessageFromPayload(await response.text(), fallbackMessage);
  }
  catch {
    return fallbackMessage;
  }
};

const requestMessageStream = async (
  conversationId: string,
  content: string,
  clientMessageId: string,
  signal: AbortSignal,
) => {
  // 注意：这里使用原生 fetch 函数来发送 SSE 请求
  const response = await fetch('/api/chat/v1/messages', {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream',
      Authorization: `Bearer ${CHANNEL_KEY}`,
      'Content-Type': 'application/json; charset=utf-8',
    },
    signal,
    body: JSON.stringify({
      conversationId,
      content,
      clientMessageId,
      lc: getCurrentLanguage(),
    }),
  });

  if (!response.ok) {
    throw new Error(await parseErrorResponse(response));
  }

  if (!response.body) {
    throw new Error(MESSAGE_ERROR_CONTENT);
  }

  return response.body;
};

const requestCancelMessage = async (messageId: string) => {
  const { isSuccess } = await $fetch(`/chat/v1/messages/${encodeURIComponent(messageId)}/cancel`, {
    method: 'post',
    headers: {
      Authorization: `Bearer ${CHANNEL_KEY}`,
    },
  });

  return isSuccess;
};

const markMessageCanceled = (assistantMessage: AssistantMessage) => {
  assistantMessage.status = 'canceled';

  if (!assistantMessage.content.trim()) {
    assistantMessage.content = MESSAGE_CANCELED_CONTENT;
  }
};

const applyStreamError = (assistantMessage: AssistantMessage, message?: string) => {
  const errorMessage = message || MESSAGE_ERROR_CONTENT;

  assistantMessage.status = 'failed';
  assistantMessage.content = errorMessage;

  return errorMessage;
};

const getStreamErrorMessage = (data: Record<string, unknown>) => {
  if (typeof data.code === 'string' && typeof data.message === 'string') {
    return data.message;
  }

  if (data.finishReason === 'error') {
    return undefined;
  }

  return null;
};

const handleSseEvent = (
  assistantMessage: AssistantMessage,
  event: SseEvent,
  currentQuestion: string,
) => {
  if (assistantMessage.status === 'canceled') {
    return undefined;
  }

  const data = isRecord(event.data) ? event.data : {};
  const streamErrorMessage = getStreamErrorMessage(data);

  if (streamErrorMessage !== null) {
    return applyStreamError(assistantMessage, streamErrorMessage);
  }

  if (event.event === 'meta') {
    assistantMessage.messageId = typeof data.messageId === 'string' ? data.messageId : assistantMessage.messageId;

    return undefined;
  }

  if (event.event === 'token') {
    const delta = typeof data.delta === 'string' ? data.delta : '';

    assistantMessage.content += delta;

    scrollMessagesToBottom();

    return undefined;
  }

  if (event.event === 'citation') {
    const citations = data.citations;

    if (Array.isArray(citations)) {
      assistantMessage.citations = citations as AssistantCitation[];
    }

    return undefined;
  }

  if (event.event === 'fallback') {
    currentFallback.value = data as unknown as AssistantFallbackPayload;

    trackKnowledgeFallback(assistantMessage, currentFallback.value);

    // 不在此处添加用户可见文案，等待后端 token 事件下发兜底回答。
    return undefined;
  }

  if (event.event === 'suggestions') {
    const items = Array.isArray(data.items) ? data.items as AssistantSuggestionItem[] : [];

    recommendedQuestions.value = normalizeSuggestions(items, currentQuestion);
    showSuggestions.value = recommendedQuestions.value.length > 0;

    if (showSuggestions.value) {
      trackSuggestionsShown(recommendedQuestions.value.length);
    }

    return undefined;
  }

  if (event.event === 'done') {
    const finishReason = typeof data.finishReason === 'string' ? data.finishReason : 'stop';

    if (finishReason === 'knowledge_miss') {
      // 新后端通常已通过 suggestions 下发；兼容没有 suggestions 的情况。
      if (!showSuggestions.value) {
        recommendedQuestions.value = normalizeSuggestions(
          sessionRecommendedQuestions.value.map(question => ({ question })),
          currentQuestion,
        );
        showSuggestions.value = recommendedQuestions.value.length > 0;

        if (showSuggestions.value) {
          trackSuggestionsShown(recommendedQuestions.value.length);
        }
      }
    }
    else {
      showSuggestions.value = false;
    }

    return undefined;
  }

  if (event.event === 'error') {
    return applyStreamError(assistantMessage, typeof data.message === 'string' ? data.message : undefined);
  }

  return undefined;
};

const sendStreamMessage = async (
  conversationId: string,
  content: string,
  clientMessageId: string,
  assistantMessage: AssistantMessage,
  receivedEventRef: { value: boolean },
  signal: AbortSignal,
) => {
  const stream = await requestMessageStream(conversationId, content, clientMessageId, signal);

  await readSseStream(stream, async (event) => {
    if (assistantMessage.status === 'canceled') {
      return;
    }

    receivedEventRef.value = true;

    const streamErrorMessage = handleSseEvent(assistantMessage, event, content);

    await nextTick();

    if (streamErrorMessage) {
      emit('error', streamErrorMessage);

      throw new Error(streamErrorMessage);
    }
  });
};

const cancelCurrentResponse = async () => {
  const streamState = activeStream.value;

  if (!streamState || streamState.assistantMessage.status !== 'streaming') {
    return;
  }

  cancelingResponse.value = true;

  markMessageCanceled(streamState.assistantMessage);

  streamState.abortController.abort();

  if (streamState.assistantMessage.messageId) {
    await requestCancelMessage(streamState.assistantMessage.messageId);
  }

  if (activeStream.value === streamState) {
    activeStream.value = null;
  }

  cancelingResponse.value = false;

  updateSessionSummary(streamState.conversationId);
  scrollMessagesToBottom();

  await focusInput();
};

const sendQuestion = async (question: string) => {
  const content = question.trim();

  if (!content || composerSendDisabled.value) {
    return;
  }

  // 用户开始下一次提问时隐藏上一轮推荐问题。
  showSuggestions.value = false;
  currentFallback.value = null;

  const currentConversationId = props.activeConversationId;
  const userMessage: AssistantMessage = {
    id: createLocalMessageId(),
    role: 'user',
    content,
    status: 'done',
  };
  const assistantMessage = reactive<AssistantMessage>({
    id: createLocalMessageId(),
    role: 'assistant',
    content: '',
    status: 'streaming',
  });
  const receivedEventRef = { value: false };
  const clientMessageId = createLocalMessageId();
  const abortController = new AbortController();

  messages.value.push(userMessage, assistantMessage);
  activeStream.value = {
    abortController,
    assistantMessage,
    conversationId: currentConversationId,
  };

  updateSessionSummary(currentConversationId);

  scrollMessagesToBottom();

  try {
    try {
      await sendStreamMessage(currentConversationId, content, clientMessageId, assistantMessage, receivedEventRef, abortController.signal);
    }
    catch (error) {
      if (isAbortError(error) || assistantMessage.status === 'canceled' || receivedEventRef.value) {
        throw error;
      }

      await sendStreamMessage(currentConversationId, content, clientMessageId, assistantMessage, receivedEventRef, abortController.signal);
    }

    if (assistantMessage.status === 'streaming') {
      assistantMessage.status = 'done';
    }
  }
  catch (error: any) {
    if (isAbortError(error) || assistantMessage.status === 'canceled') {
      markMessageCanceled(assistantMessage);
    }
    else {
      assistantMessage.status = 'failed';
      assistantMessage.content = error?.message || MESSAGE_ERROR_CONTENT;

      emit('error', assistantMessage.content);
    }
  }
  finally {
    if (activeStream.value?.assistantMessage === assistantMessage) {
      activeStream.value = null;
    }

    cancelingResponse.value = false;

    // 切换会话后旧流结束，此时 messages 已属于新会话，跳过旧会话摘要更新。
    if (messages.value.includes(userMessage)) {
      updateSessionSummary(currentConversationId);
    }

    scrollMessagesToBottom();

    await focusInput();
  }
};

const consumePendingQuestion = () => {
  const pendingQuestion = props.pendingQuestion;

  if (
    !pendingQuestion
    || processingPendingQuestionId.value === pendingQuestion.id
    || loadingHistory.value
    || composerSendDisabled.value
  ) {
    return;
  }

  processingPendingQuestionId.value = pendingQuestion.id;

  void sendQuestion(pendingQuestion.content).finally(() => {
    emit('pendingQuestionHandled', pendingQuestion.id);
    processingPendingQuestionId.value = null;
  });
};

const handleSuggestionSend = (question: string) => {
  trackAction(TRACK_EVENT_SUGGESTION_CLICK, {
    conversationId: props.activeConversationId,
    index: recommendedQuestions.value.indexOf(question),
  });

  void sendQuestion(question);
};

watch(() => props.activeConversationId, loadHistory, { immediate: true });

watch(() => props.questions, (questions) => {
  const prompts = questions.map(question => question.prompt);

  sessionRecommendedQuestions.value = prompts;

  // 正在响应时不打断推荐区域当前的显示状态（如提问中建会话完成）。
  if (!isResponding.value) {
    recommendedQuestions.value = prompts;
    showSuggestions.value = prompts.length > 0;
  }
}, { immediate: true });

watch(
  [() => props.pendingQuestion, () => props.activeConversationId, loadingHistory, isResponding],
  consumePendingQuestion,
);

defineExpose({
  focusInput,
  scrollMessagesToBottom,
});
</script>

<template>
  <div v-if="props.activeConversationId" class="h-full min-h-0 flex flex-col">
    <AssistantMessageList
      ref="messageList"
      :loading="loadingHistory"
      :messages="messages"
    />

    <AssistantQuickQuestions
      :disabled="composerSendDisabled"
      :questions="visibleQuestions"
      @send="handleSuggestionSend"
    />

    <AssistantComposer
      ref="composer"
      :canceling="cancelingResponse"
      :disabled="composerInputDisabled"
      :responding="isResponding"
      :send-disabled="composerSendDisabled"
      @cancel="cancelCurrentResponse"
      @send="sendQuestion"
    />
  </div>

  <div v-else class="h-full flex-center p-18 text-14 text-#6B7280">
    正在创建会话...
  </div>
</template>
